// Project-scoped photo coordinates; never infer coordinates from an image stamp.
export function hasPhotoGPS(photo) {
  const lat = photo?.gps_latitude;
  const lng = photo?.gps_longitude;
  return typeof lat === "number" && typeof lng === "number" &&
    Number.isFinite(lat) && Number.isFinite(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 &&
    !(lat === 0 && lng === 0);
}

export function formatGPS(photo) {
  if (!hasPhotoGPS(photo)) return "No stored GPS coordinates";
  // Show stored numeric precision, never silently limit to 5 decimals.
  return `${photo.gps_latitude}, ${photo.gps_longitude}`;
}

export function gpsSourceLabel(photo) {
  if (!hasPhotoGPS(photo)) return "No GPS";
  if (photo.gps_source === "exif") return "Original EXIF GPS";
  if (photo.gps_source === "image_overlay") return "Confirmed GPS from image text";
  return "Stored GPS (source unverified)";
}

// Web Mercator, same projection used by raster street-map tiles.
export function projectPoint(lat, lng, zoom) {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const radians = clamped * Math.PI / 180;
  const size = 256 * 2 ** zoom;
  return {
    x: ((lng + 180) / 360) * size,
    y: (1 - Math.log(Math.tan(radians) + 1 / Math.cos(radians)) / Math.PI) * size / 2,
  };
}

export function unprojectPoint(x, y, zoom) {
  const size = 256 * 2 ** zoom;
  return {
    lat: (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - 2 * y / size))),
    lng: ((x / size) * 360) - 180,
  };
}

export function fitPhotoBounds(photos, width = 400, height = 310) {
  const located = photos.filter(hasPhotoGPS);
  if (!located.length) return { lat: 0, lng: 0, zoom: 3 };
  const projected = located.map(p => projectPoint(p.gps_latitude, p.gps_longitude, 0));
  const a = Math.min(...projected.map(p => p.x));
  const b = Math.max(...projected.map(p => p.x));
  const c = Math.min(...projected.map(p => p.y));
  const d = Math.max(...projected.map(p => p.y));
  const center = unprojectPoint((a + b) / 2, (c + d) / 2, 0);
  let zoom = 3;
  for (let z = 17; z >= 3; z--) {
    const factor = 2 ** z;
    if ((b - a) * factor <= Math.max(100, width - 110) &&
        (d - c) * factor <= Math.max(100, height - 105)) {
      zoom = z;
      break;
    }
  }
  return { ...center, zoom };
}

export function clusterPhotos(photos, center, zoom, width, height, cellSize = 44) {
  const origin = projectPoint(center.lat, center.lng, zoom);
  const bins = new Map();
  for (const photo of photos.filter(hasPhotoGPS)) {
    const pos = projectPoint(photo.gps_latitude, photo.gps_longitude, zoom);
    const x = pos.x - origin.x + width / 2;
    const y = pos.y - origin.y + height / 2;
    if (x < -cellSize || x > width + cellSize || y < -cellSize || y > height + cellSize) continue;
    const key = `${Math.round(x / cellSize)}:${Math.round(y / cellSize)}`;
    if (!bins.has(key)) bins.set(key, { x, y, photos: [] });
    bins.get(key).photos.push(photo);
  }
  return [...bins.values()];
}

export function openMapUrl(photo) {
  if (!hasPhotoGPS(photo)) return null;
  const lat = encodeURIComponent(photo.gps_latitude);
  const lng = encodeURIComponent(photo.gps_longitude);
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=18/${lat}/${lng}`;
}
